import { Grid3x3, List } from "lucide-react";

import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

export type CardListFilter = "all" | "virtual" | "fisica" | "frozen" | "pending" | "no_pocket";

export type CardListView = "list" | "grid";

// Combined `${sort_by}:${order}` value so a single select drives both fields.
export type CardListSortValue = "created_at:DESC" | "created_at:ASC" | "balance:DESC" | "balance:ASC";

type CardsListFiltersBarProps = {
  filter: CardListFilter;
  onFilterChange: (next: CardListFilter) => void;
  query: string;
  onQueryChange: (next: string) => void;
  view: CardListView;
  onViewChange: (next: CardListView) => void;
  sortValue: CardListSortValue;
  onSortChange: (next: CardListSortValue) => void;
  counts: Record<CardListFilter, number>;
  /** Virtual / physical only split the list once a physical card exists. */
  showProductFilters: boolean;
  showPocketFilter: boolean;
};

const SORT_OPTIONS: ReadonlyArray<{ value: CardListSortValue; labelKey: string }> = [
  { value: "created_at:DESC", labelKey: "cards_sort_newest" },
  { value: "created_at:ASC", labelKey: "cards_sort_oldest" },
  { value: "balance:DESC", labelKey: "cards_sort_balance_high" },
  { value: "balance:ASC", labelKey: "cards_sort_balance_low" },
];

const FILTER_OPTIONS: ReadonlyArray<{
  id: CardListFilter;
  labelKey: string;
}> = [
  { id: "all", labelKey: "cards_filter_all" },
  { id: "virtual", labelKey: "cards_filter_virtual" },
  { id: "fisica", labelKey: "cards_filter_fisica" },
  { id: "frozen", labelKey: "cards_filter_frozen" },
  { id: "pending", labelKey: "cards_filter_pending" },
  { id: "no_pocket", labelKey: "cards_filter_no_pocket" },
];

export const CardsListFiltersBar = ({
  filter,
  onFilterChange,
  query,
  onQueryChange,
  view,
  onViewChange,
  sortValue,
  onSortChange,
  counts,
  showProductFilters,
  showPocketFilter,
}: CardsListFiltersBarProps) => {
  const { t } = useTranslations("cards");
  // The active filter always keeps its chip, so a link opened on a hidden one still shows what is applied.
  const isHidden = (id: CardListFilter) =>
    ((id === "virtual" || id === "fisica") && !showProductFilters) ||
    (id === "no_pocket" && !showPocketFilter);
  const visibleFilters = FILTER_OPTIONS.filter((option) => option.id === filter || !isHidden(option.id));

  const sortOptions = SORT_OPTIONS.map((option) => ({
    value: option.value,
    label: t(option.labelKey as never),
  }));

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {visibleFilters.map((option) => {
          const active = option.id === filter;
          const count = counts[option.id];
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => onFilterChange(option.id)}
              className={cn(
                "inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-md)] border px-2.5 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                active
                  ? "border-foreground bg-foreground text-background"
                  : "border-border bg-background text-foreground hover:bg-muted",
              )}
            >
              {t(option.labelKey as never)}
              <span
                className={cn(
                  "font-mono text-[10px]",
                  active ? "text-background/70" : "text-muted-foreground",
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <ColocaSearchInput
          value={query}
          onValueChange={onQueryChange}
          placeholder={t("cards_search_placeholder")}
          className="sm:w-[260px]"
        />

        <ColocaSelect
          aria-label={t("cards_sort_label")}
          value={sortValue}
          onValueChange={(next) => {
            if (next) onSortChange(next as CardListSortValue);
          }}
          options={sortOptions}
          className="w-[170px]"
        />

        <div className="inline-flex rounded-[var(--radius-md)] border border-border bg-background p-0.5">
          <button
            type="button"
            onClick={() => onViewChange("list")}
            aria-label={t("cards_view_list")}
            aria-pressed={view === "list"}
            className={cn(
              "inline-flex h-7 w-7 items-center justify-center rounded-[6px] transition-colors",
              view === "list" ? "bg-muted text-foreground" : "text-muted-foreground",
            )}
          >
            <List className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onViewChange("grid")}
            aria-label={t("cards_view_grid")}
            aria-pressed={view === "grid"}
            className={cn(
              "inline-flex h-7 w-7 items-center justify-center rounded-[6px] transition-colors",
              view === "grid" ? "bg-muted text-foreground" : "text-muted-foreground",
            )}
          >
            <Grid3x3 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};
